import { BaseRequest } from "../../../../../base/request/BaseRequest";
import { errorService } from "../../../../../comun/services/ErrorService";
import { fx_createRestablecerPasswordRequest } from "../../../../../modulos/seguridad/password/request/RestablecerPasswordRequest";
import {
  parsePasswordResetError,
  passwordResetErrorMessage,
} from "../../../../../modulos/seguridad/password/response/PasswordResetErrorResponse";
import { restablecerPasswordGrpcService as restablecerPasswordService } from "../../../../../modulos/seguridad/password/services/RestablecerPasswordGrpcService";
import { CardConnectRender } from "../../../../base/card/CardConnectRender";
import { CardBody } from "../../../../definitions/card/CardBody";
import { CardFooter } from "../../../../definitions/card/CardFooter";
import { CardHeader } from "../../../../definitions/card/CardHeader";
import { TextConfig } from "../../../../definitions/form/inputs/config/TextConfig";
import { IFormField } from "../../../../definitions/form/IFormField";
import { TextField } from "../../../../implementations/form/text/TextField";

/** Política de contraseña del servidor, espejada en el cliente para validar
 *  antes de enviar. `\p{L}`/`\p{Nd}` en vez de `[a-zA-Z]`/`[0-9]`: el servidor
 *  acepta Unicode, así que una contraseña con letras acentuadas o dígitos no
 *  ASCII es válida y no hay que rechazarla acá. */
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 100;
const PASSWORD_LETRA_REGEX = /\p{L}/u;
const PASSWORD_DIGITO_REGEX = /\p{Nd}/u;

/** Modelo del formulario del paso 3. La confirmación es UI-only: nunca viaja al
 *  servidor (el request se arma con `resetToken` + `password` al enviar). */
type RestablecerPasswordForm = BaseRequest & {
  password: string;
  confirmacion: string;
};

export type RestablecerPasswordCardConfig = {
  /** Token de un solo uso obtenido en el paso 2. */
  resetToken: string;
  /** Expiración del token, en milisegundos Unix. */
  expiraEn: number;
  /** Contraseña cambiada: la sesión local ya quedó limpia. */
  onRestablecido: () => void;
  /** El token no sirve (venció o el servidor lo rechazó): reiniciar el flujo. */
  onTokenInvalido: () => void;
  /** El usuario abandonó el flujo (botón back del header). */
  onCancel: () => void;
};

/** Paso 3 del flujo "olvidé mi contraseña": define la contraseña nueva. */
export class RestablecerPasswordCardRender extends CardConnectRender {
  private readonly _config: RestablecerPasswordCardConfig;
  private readonly _form: RestablecerPasswordForm;
  private readonly _passwordField: TextField<RestablecerPasswordForm>;
  private readonly _confirmacionField: TextField<RestablecerPasswordForm>;
  private readonly _fields: IFormField[];

  constructor(config: RestablecerPasswordCardConfig) {
    super();
    this._config = config;
    this._form = { password: "", confirmacion: "" };
    this._passwordField = this._createPasswordField();
    this._confirmacionField = this._createConfirmacionField();
    this._fields = [this._passwordField, this._confirmacionField];
    this._backAction = () => this._config.onCancel();
  }

  protected getCardHeader(): CardHeader {
    return {
      title: "Nueva contraseña",
      subtitle: "Al menos 8 caracteres, con letras y números",
    };
  }

  protected getCardBody(): CardBody {
    return {
      additionalCssClass: "",
      content: this._fields.map((field) => field.render()).join(""),
    };
  }

  protected getCardFooter(): CardFooter | null {
    return {
      additionalCssClass: "",
      content: `<button class="sx-btn sx-btn-primary sx-btn-block" type="button" id="restablecer-submit-${this._cardId}">Guardar contraseña</button>`,
    };
  }

  protected afterConnect(): void {
    this._fields.forEach((field) => field.connect());

    this._submitButton = document.getElementById(
      `restablecer-submit-${this._cardId}`,
    ) as HTMLButtonElement;
    this._submitButton.addEventListener("click", this._onSubmitClick);
  }

  protected beforeDispose(): void {
    this._submitButton?.removeEventListener("click", this._onSubmitClick);
    this._fields.forEach((field) => field.dispose());
  }

  private _submitButton: HTMLButtonElement | null = null;
  /** Error del servidor sobre la contraseña (se limpia al editar). */
  private _serverError: string | null = null;

  private _createPasswordField(): TextField<RestablecerPasswordForm> {
    const config: TextConfig<RestablecerPasswordForm> = {
      get(form) {
        return form.password;
      },
      set(form, newValue) {
        form.password = newValue;
      },
      label: "Contraseña nueva",
      type: "password",
      icon: "ph-lock-key",
      autocomplete: "new-password",
    };
    const field = new TextField<RestablecerPasswordForm>(this._form, config, {
      isRequired: true,
      minLength: PASSWORD_MIN_LENGTH,
      maxLength: PASSWORD_MAX_LENGTH,
      custom: (value) => {
        if (this._serverError) return this._serverError;
        if (!PASSWORD_LETRA_REGEX.test(value))
          return "Debe incluir al menos una letra";
        if (!PASSWORD_DIGITO_REGEX.test(value))
          return "Debe incluir al menos un número";
        return null;
      },
    });
    field.setOnChange(() => {
      this._serverError = null;
    });
    return field;
  }

  private _createConfirmacionField(): TextField<RestablecerPasswordForm> {
    const config: TextConfig<RestablecerPasswordForm> = {
      get(form) {
        return form.confirmacion;
      },
      set(form, newValue) {
        form.confirmacion = newValue;
      },
      label: "Repetir contraseña",
      type: "password",
      icon: "ph-lock-key",
      autocomplete: "new-password",
    };
    return new TextField<RestablecerPasswordForm>(this._form, config, {
      isRequired: true,
      custom: (value) =>
        value === this._form.password ? null : "Las contraseñas no coinciden",
    });
  }

  private _onSubmitClick = (): void => {
    if (!this._validate()) return;

    // El token ya venció: no tiene sentido gastar el request, el servidor
    // respondería TOKEN_EXPIRADO.
    if (Date.now() > this._config.expiraEn) {
      errorService.addWarning(
        "El código venció",
        "Pedí un código nuevo para cambiar tu contraseña",
      );
      this._config.onTokenInvalido();
      return;
    }

    this._setBusy(true);
    restablecerPasswordService
      .restablecerAsync(
        fx_createRestablecerPasswordRequest(
          this._config.resetToken,
          this._form.password,
        ),
      )
      .then(() => {
        errorService.addSuccess(
          "Contraseña actualizada",
          "Ingresá con tu contraseña nueva",
        );
        this._config.onRestablecido();
      })
      .catch((error: unknown) => {
        this._handleError(error);
      });
  };

  /** Valida ambos campos y deja el foco en el primero inválido. */
  private _validate(): boolean {
    this._fields.forEach((field) => field.showError());

    const firstInvalid = this._fields.find((field) => field.getError());
    if (!firstInvalid) return true;

    firstInvalid.focus();
    return false;
  }

  /** `PASSWORD_DEBIL` deja corregir en el mismo paso; un token inválido o
   *  vencido obliga a rehacer el flujo desde el paso 1. */
  private _handleError(error: unknown): void {
    const parsed = parsePasswordResetError(error);

    switch (parsed?.code) {
      case "PASSWORD_DEBIL":
        this._serverError = parsed.message;
        this._passwordField.showError();
        this._passwordField.focus();
        this._setBusy(false);
        return;

      case "TOKEN_INVALIDO":
      case "TOKEN_EXPIRADO":
        errorService.addWarning("No se pudo cambiar la contraseña", parsed.message);
        this._config.onTokenInvalido();
        return;

      default:
        errorService.addError(
          "No se pudo cambiar la contraseña",
          parsed?.message ?? passwordResetErrorMessage(error),
        );
        this._setBusy(false);
    }
  }

  private _setBusy(isBusy: boolean): void {
    if (this._submitButton) this._submitButton.disabled = isBusy;
    this._fields.forEach((field) =>
      isBusy ? field.disabled() : field.enabled(),
    );
  }
}
